import { useEffect, useState } from "react";
import { Crown, Rocket, Building2, Loader2, Trash2, Send, Copy, X, ShieldCheck, ShieldOff } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

/**
 * iter80 — Platform Roles tab (lives in Platform Console).
 *
 * Reads/mutates the `platform_users` collection — completely separate from
 * the `site_admins` (tenant) collection.
 *
 * Capabilities (platform_admin only — developer view is read-only):
 *   - Invite a new Commons Ops / Observer by email (magic link via Resend).
 *   - List Commons Admins (read-only — env-seed controlled).
 *   - List Commons Ops + Observers + revoke / delete.
 *   - List pending invites + copy magic link + cancel.
 */

function InviteForm({ onInvited }) {
    const [email, setEmail] = useState("");
    const [name, setName] = useState("");
    const [role, setRole] = useState("platform_developer");
    const [busy, setBusy] = useState(false);
    const [lastMagicLink, setLastMagicLink] = useState(null);

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        try {
            const r = await api.platformInvite(email.trim(), name.trim() || null, role);
            if (r.email_sent) {
                toast.success(`Invite sent to ${r.email}`);
            } else {
                toast.warning("Invite created, but email delivery failed — copy the magic link below and send it manually.");
            }
            setLastMagicLink(r.magic_link_preview_only);
            setEmail("");
            setName("");
            onInvited();
        } catch (err) {
            const msg = err.response?.data?.detail || "Invite failed";
            toast.error(typeof msg === "string" ? msg : "Invite failed");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="brutal-card p-4" data-testid="platform-invite-form">
            <div className="flex items-center gap-2 mb-2">
                <Send className="w-4 h-4 text-volt" />
                <h3 className="font-display font-extrabold text-sm uppercase tracking-wide">Invite Commons Admin / Ops / Observer</h3>
            </div>
            <p className="text-xs text-zinc-500 mb-3">
                They'll get an email with a magic link to set their own password. Email expires in 7 days. Owner elevation is env-seed only; invited Commons Admins start with every permission toggle off (view-only).
            </p>
            <form onSubmit={submit} className="space-y-2 sm:flex sm:items-end sm:gap-2 sm:space-y-0">
                <div className="flex-1">
                    <label className="text-[10px] font-bold uppercase text-zinc-400">Email</label>
                    <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        data-testid="platform-invite-email-input"
                        className="brutal-input w-full mt-1"
                        placeholder="dev@example.com"
                    />
                </div>
                <div className="flex-1">
                    <label className="text-[10px] font-bold uppercase text-zinc-400">Name (optional)</label>
                    <input
                        type="text" value={name} onChange={(e) => setName(e.target.value)}
                        data-testid="platform-invite-name-input"
                        className="brutal-input w-full mt-1"
                        placeholder="Jane Dev"
                    />
                </div>
                <div>
                    <label className="text-[10px] font-bold uppercase text-zinc-400">Role</label>
                    <select
                        value={role} onChange={(e) => setRole(e.target.value)}
                        data-testid="platform-invite-role-select"
                        className="brutal-input w-full mt-1 bg-ink cursor-pointer"
                    >
                        <option value="platform_developer">Commons Ops</option>
                        <option value="platform_observer">Commons Observer</option>
                        <option value="platform_admin">Commons Admin (co-admin)</option>
                    </select>
                </div>
                <button type="submit" disabled={busy} data-testid="platform-invite-submit-btn" className="brutal-btn">
                    {busy ? "Sending…" : "Send invite"}
                </button>
            </form>
            {lastMagicLink && (
                <div className="mt-3 p-2 border-2 border-volt/30 bg-volt/5 text-[10px] break-all" data-testid="platform-invite-magic-link">
                    <p className="font-bold text-volt mb-1 flex items-center gap-2">
                        Magic link (also emailed):
                        <button
                            type="button"
                            onClick={() => { navigator.clipboard.writeText(lastMagicLink); toast.success("Copied"); }}
                            data-testid="copy-magic-link-btn"
                            className="brutal-btn-ghost text-[10px] py-1 px-2"
                        ><Copy className="w-3 h-3" /> Copy</button>
                    </p>
                    <code className="text-zinc-300">{lastMagicLink}</code>
                </div>
            )}
        </div>
    );
}

function RoleReferenceCards() {
    return (
        <div className="grid sm:grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-3" data-testid="role-reference-cards">
            <div className="brutal-card p-3 border-2 border-cyber/40 bg-cyber/5" data-testid="role-card-platform-admin">
                <div className="flex items-center gap-2 mb-1">
                    <Crown className="w-4 h-4 text-cyber" />
                    <h3 className="font-display font-extrabold uppercase text-xs">Commons Owner / Admin</h3>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">Owner: env-seed, full power incl. this tab. Co-admins: invitable below — start view-only with per-area permission toggles (Tenant / Product / Releases / Compliance / Pulse / Stewardship).</p>
            </div>
            <div className="brutal-card p-3 border-2 border-volt/40 bg-volt/5" data-testid="role-card-platform-developer">
                <div className="flex items-center gap-2 mb-1">
                    <Rocket className="w-4 h-4 text-volt" />
                    <h3 className="font-display font-extrabold uppercase text-xs">Commons Ops</h3>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">Hands on Rhythm + Rehearsal Ops; Pulse & Canon read-only. No tenant data. Invitable below.</p>
            </div>
            <div className="brutal-card p-3 border-2 border-zinc-500/40 bg-zinc-500/5" data-testid="role-card-platform-observer">
                <div className="flex items-center gap-2 mb-1">
                    <ShieldCheck className="w-4 h-4 text-zinc-300" />
                    <h3 className="font-display font-extrabold uppercase text-xs">Commons Observer</h3>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">Read-only everywhere (except this tab). Can navigate every console area but modify nothing. Invitable below.</p>
            </div>
            <div className="brutal-card p-3 border-2 border-[#333]" data-testid="role-card-tenant-admin">
                <div className="flex items-center gap-2 mb-1">
                    <Building2 className="w-4 h-4 text-zinc-400" />
                    <h3 className="font-display font-extrabold uppercase text-xs">Tenant Admin</h3>
                </div>
                <p className="text-[11px] text-zinc-400 leading-snug">Lives in <span className="font-mono text-cyber">site_admins</span> — separate collection. Managed from <a href="/admin" className="underline">/admin</a>.</p>
            </div>
        </div>
    );
}

export const ADMIN_PERMS = [
    ["tenant_ops", "Tenant Ops"],
    ["product_ops", "Product Ops"],
    ["release_vendors", "Releases & Vendors"],
    ["compliance_ops", "Compliance"],
    ["pulse", "Pulse"],
    ["stewardship", "Stewardship"],
];

export default function PlatformRolesTab() {
    const [users, setUsers] = useState(null);
    const [pending, setPending] = useState([]);
    const [me, setMe] = useState(null);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [selected, setSelected] = useState(new Set());

    const reload = async () => {
        try {
            const [u, p, m] = await Promise.all([
                api.platformListUsers(),
                api.platformListPendingInvites().catch(() => []),
                api.authMe(),
            ]);
            setUsers(u);
            setPending(p);
            setMe(m);
        } catch (err) {
            const msg = err.response?.data?.detail || "Failed to load platform roles";
            toast.error(typeof msg === "string" ? msg : "Failed");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { reload(); }, []);

    const isAdmin = me?.role === "platform_admin";

    const revoke = async (user) => {
        if (!window.confirm(`Revoke Commons Console access from ${user.email}? They will lose access immediately.`)) return;
        setBusyId(user.id);
        try {
            await api.platformDeleteUser(user.id);
            toast.success(`Removed ${user.email}`);
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed");
        } finally {
            setBusyId(null);
        }
    };

    const resetTotp = async (user) => {
        if (!window.confirm(`Clear ${user.email}'s two-factor setup? They'll be able to sign in with password alone and re-enroll. An audit-log row will be written.`)) return;
        setBusyId(user.id + "totp");
        try {
            await api.authSuperAdminResetTotp(user.id);
            toast.success(`Cleared 2FA on ${user.email} — they can now re-enroll`);
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Reset didn't go through");
        } finally {
            setBusyId(null);
        }
    };

    const togglePerm = async (user, key) => {
        const next = !((user.permissions || {})[key]);
        setBusyId(user.id + key);
        try {
            await api.platformSetPermissions(user.id, { [key]: next });
            const label = ADMIN_PERMS.find(([k]) => k === key)[1];
            toast.success(`${label} ${next ? "enabled" : "disabled"} for ${user.email}`);
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Failed");
        } finally {
            setBusyId(null);
        }
    };

    const cancelInvite = async (inv) => {
        if (!window.confirm(`Cancel pending invite for ${inv.email}? The magic link stops working immediately.`)) return;
        setBusyId(inv.token_prefix);
        try {
            await api.platformCancelInvite(inv.token_prefix);
            toast.success(`Invite for ${inv.email} cancelled`);
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Cancel failed");
        } finally {
            setBusyId(null);
        }
    };

    const resendInvite = async (inv) => {
        setBusyId(inv.token_prefix);
        try {
            const r = await api.platformResendInvite(inv.token_prefix);
            if (r.email_sent) {
                toast.success(`Invite re-sent to ${r.email} — expiry extended 7 days`);
            } else {
                try { await navigator.clipboard.writeText(r.magic_link_preview_only); } catch { /* clipboard unavailable */ }
                toast.warning("Email delivery failed (check the Resend sending domain) — magic link copied to your clipboard instead.");
            }
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Resend failed");
        } finally {
            setBusyId(null);
        }
    };

    const toggleSelect = (id) => setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(id)) next.delete(id); else next.add(id);
        return next;
    });

    const bulkDelete = async () => {
        if (!window.confirm(`Delete ${selected.size} Commons user(s)? They lose Commons Console access immediately.`)) return;
        setBusyId("bulk");
        try {
            const r = await api.platformBulkDeleteUsers([...selected]);
            toast.success(`Deleted ${r.deleted_count} user(s)`);
            setSelected(new Set());
            await reload();
        } catch (err) {
            const msg = err.response?.data?.detail;
            toast.error(typeof msg === "string" ? msg : "Bulk delete failed");
        } finally {
            setBusyId(null);
        }
    };

    if (loading) {
        return (
            <div className="brutal-card p-6 text-center" data-testid="platform-roles-loading">
                <Loader2 className="w-6 h-6 animate-spin mx-auto text-zinc-500" />
                <p className="text-xs text-zinc-500 mt-2">Loading…</p>
            </div>
        );
    }

    const admins = (users || []).filter((u) => u.role === "platform_admin");
    const developers = (users || []).filter((u) => u.role === "platform_developer");
    const observers = (users || []).filter((u) => u.role === "platform_observer");

    return (
        <div className="space-y-5" data-testid="platform-roles-tab">
            <div>
                <h2 className="font-display font-extrabold text-2xl uppercase flex items-center gap-2">
                    <Crown className="w-6 h-6 text-volt" />
                    Commons Roles
                </h2>
                <p className="text-xs text-zinc-500 mt-1">
                    Source of truth for platform-tier identities. Tenant admins live in a separate collection and are managed from <a href="/admin" className="underline text-cyber">/admin</a>.
                </p>
            </div>

            <RoleReferenceCards />

            {isAdmin && <InviteForm onInvited={reload} />}

            {/* iter86.20.2 — bulk delete action bar */}
            {isAdmin && selected.size > 0 && (
                <div className="brutal-card p-3 flex items-center gap-3 border-2 border-red-500/60 bg-red-500/5" data-testid="bulk-delete-bar">
                    <span className="text-xs font-bold" data-testid="bulk-selected-count">{selected.size} selected</span>
                    <button
                        onClick={bulkDelete}
                        disabled={busyId === "bulk"}
                        data-testid="bulk-delete-users-btn"
                        className="brutal-btn text-[10px] px-3 py-1.5 bg-red-600 text-white disabled:opacity-50"
                    >
                        <Trash2 className="w-3 h-3" /> {busyId === "bulk" ? "Deleting…" : "Delete selected"}
                    </button>
                    <button
                        onClick={() => setSelected(new Set())}
                        data-testid="bulk-clear-selection-btn"
                        className="brutal-btn-ghost text-[10px] px-2 py-1.5"
                    >
                        Clear
                    </button>
                </div>
            )}

            {/* Commons Admins */}
            <div className="brutal-card p-4" data-testid="platform-admins-list">
                <div className="flex items-center gap-2 mb-3">
                    <Crown className="w-4 h-4 text-cyber" />
                    <h3 className="font-display font-extrabold uppercase text-sm">Commons Admins · {admins.length}</h3>
                    <span className="pill text-[9px] font-bold uppercase border bg-zinc-500/15 text-zinc-400 border-zinc-500/40">Owner env-seed · co-admins invitable</span>
                </div>
                {admins.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">None — your env seed didn't run.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {admins.map((u) => (
                            <li key={u.id} data-testid={`admin-row-${u.id}`} className="text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                <div className="flex items-center gap-2">
                                    {isAdmin && u.id !== me?.id && !u.is_super && (
                                        <input
                                            type="checkbox"
                                            checked={selected.has(u.id)}
                                            onChange={() => toggleSelect(u.id)}
                                            data-testid={`bulk-select-user-${u.id}`}
                                            className="accent-volt w-3.5 h-3.5 shrink-0 cursor-pointer"
                                        />
                                    )}
                                    <div className="flex-1 min-w-0">
                                        <span className="font-bold text-cyber">{u.email}</span>
                                        {u.is_super && <span className="pill text-[9px] font-bold uppercase border bg-cyber/15 text-cyber border-cyber/40 ml-2">Owner</span>}
                                        {u.id === me?.id && <span className="text-[10px] text-volt ml-2">(you)</span>}
                                        <span className="text-zinc-500 ml-2">· {u.name || "—"}</span>
                                    </div>
                                    <span className="text-[10px] text-zinc-600">since {new Date(u.created_at).toLocaleDateString()}</span>
                                    {isAdmin && me?.is_super && u.id !== me?.id && (
                                        <button
                                            onClick={() => resetTotp(u)}
                                            disabled={busyId === u.id + "totp"}
                                            data-testid={`reset-totp-${u.id}-btn`}
                                            className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                            title="Reset 2FA (super admin only) — clears their authenticator so they can re-enroll"
                                        >
                                            <ShieldOff className="w-3 h-3 text-volt" />
                                            {busyId === u.id + "totp" ? "…" : "Reset 2FA"}
                                        </button>
                                    )}
                                    {isAdmin && u.id !== me?.id && !u.is_super && (
                                        <button
                                            onClick={() => revoke(u)}
                                            disabled={busyId === u.id}
                                            data-testid={`revoke-admin-${u.id}-btn`}
                                            className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                        >
                                            <Trash2 className="w-3 h-3" />
                                            {busyId === u.id ? "…" : "Remove"}
                                        </button>
                                    )}
                                </div>
                                {!u.is_super && (
                                    <div className="flex flex-wrap items-center gap-1.5 mt-1.5 ml-5" data-testid={`admin-perms-${u.id}`}>
                                        <span className="text-[9px] font-bold uppercase text-zinc-600">Permissions:</span>
                                        {ADMIN_PERMS.map(([key, label]) => {
                                            const on = !!((u.permissions || {})[key]);
                                            return (
                                                <button
                                                    key={key}
                                                    onClick={() => togglePerm(u, key)}
                                                    disabled={busyId === u.id + key}
                                                    data-testid={`perm-toggle-${key}-${u.id}`}
                                                    title={`${label}: ${on ? "enabled — click to disable" : "disabled — click to enable"}`}
                                                    className={`pill text-[9px] font-bold uppercase border px-2 py-0.5 transition-colors disabled:opacity-50 ${on ? "bg-volt/20 text-volt border-volt/50" : "bg-zinc-800/60 text-zinc-500 border-zinc-700"}`}
                                                >
                                                    {on ? "✓ " : ""}{label}
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Commons Ops */}
            <div className="brutal-card p-4" data-testid="platform-developers-list">
                <div className="flex items-center gap-2 mb-3">
                    <Rocket className="w-4 h-4 text-volt" />
                    <h3 className="font-display font-extrabold uppercase text-sm">Commons Ops · {developers.length}</h3>
                </div>
                {developers.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">No Commons Ops yet — invite someone above.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {developers.map((u) => (
                            <li key={u.id} data-testid={`developer-row-${u.id}`} className="flex items-center gap-2 text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                {isAdmin && u.id !== me?.id && (
                                    <input
                                        type="checkbox"
                                        checked={selected.has(u.id)}
                                        onChange={() => toggleSelect(u.id)}
                                        data-testid={`bulk-select-user-${u.id}`}
                                        className="accent-volt w-3.5 h-3.5 shrink-0 cursor-pointer"
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <span className="font-bold text-volt">{u.email}</span>
                                    {u.id === me?.id && <span className="text-[10px] text-cyber ml-2">(you)</span>}
                                    <span className="text-zinc-500 ml-2">· {u.name || "—"}</span>
                                    <span className="text-[10px] text-zinc-600 ml-2">since {new Date(u.created_at).toLocaleDateString()}</span>
                                </div>
                                {isAdmin && u.id !== me?.id && (
                                    <button
                                        onClick={() => revoke(u)}
                                        disabled={busyId === u.id}
                                        data-testid={`revoke-developer-${u.id}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        {busyId === u.id ? "…" : "Remove"}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Commons Observers */}
            <div className="brutal-card p-4" data-testid="platform-observers-list">
                <div className="flex items-center gap-2 mb-3">
                    <ShieldCheck className="w-4 h-4 text-zinc-300" />
                    <h3 className="font-display font-extrabold uppercase text-sm">Commons Observers · {observers.length}</h3>
                    <span className="pill text-[9px] font-bold uppercase border bg-zinc-500/15 text-zinc-400 border-zinc-500/40">Read-only role</span>
                </div>
                {observers.length === 0 ? (
                    <p className="text-xs text-zinc-500 italic">No observers yet — invite someone above.</p>
                ) : (
                    <ul className="space-y-1.5">
                        {observers.map((u) => (
                            <li key={u.id} data-testid={`observer-row-${u.id}`} className="flex items-center gap-2 text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                {isAdmin && u.id !== me?.id && (
                                    <input
                                        type="checkbox"
                                        checked={selected.has(u.id)}
                                        onChange={() => toggleSelect(u.id)}
                                        data-testid={`bulk-select-user-${u.id}`}
                                        className="accent-volt w-3.5 h-3.5 shrink-0 cursor-pointer"
                                    />
                                )}
                                <div className="min-w-0 flex-1">
                                    <span className="font-bold text-zinc-300">{u.email}</span>
                                    {u.id === me?.id && <span className="text-[10px] text-cyber ml-2">(you)</span>}
                                    <span className="text-zinc-500 ml-2">· {u.name || "—"}</span>
                                    <span className="text-[10px] text-zinc-600 ml-2">since {new Date(u.created_at).toLocaleDateString()}</span>
                                </div>
                                {isAdmin && u.id !== me?.id && (
                                    <button
                                        onClick={() => revoke(u)}
                                        disabled={busyId === u.id}
                                        data-testid={`revoke-observer-${u.id}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        {busyId === u.id ? "…" : "Remove"}
                                    </button>
                                )}
                            </li>
                        ))}
                    </ul>
                )}
            </div>

            {/* Pending invites */}
            {isAdmin && pending.length > 0 && (
                <div className="brutal-card p-4" data-testid="pending-invites-list">
                    <div className="flex items-center gap-2 mb-3">
                        <Send className="w-4 h-4 text-zinc-400" />
                        <h3 className="font-display font-extrabold uppercase text-sm">Pending invites · {pending.length}</h3>
                    </div>
                    <ul className="space-y-1.5">
                        {pending.map((inv) => (
                            <li key={inv.email + inv.created_at} data-testid={`pending-invite-row-${inv.token_prefix}`} className="flex items-center justify-between gap-2 text-xs py-1.5 border-b border-[#1a1a1a] last:border-b-0">
                                <div className="min-w-0 flex-1">
                                    <span className="font-bold text-zinc-300">{inv.email}</span>
                                    <span className="text-zinc-500 ml-2">· {inv.role}</span>
                                    <span className="text-[10px] text-zinc-600 ml-2">expires {new Date(inv.expires_at).toLocaleDateString()}</span>
                                    {inv.resent_at && <span className="text-[10px] text-volt ml-2">re-sent</span>}
                                </div>
                                <div className="flex gap-1.5 shrink-0">
                                    <button
                                        onClick={() => resendInvite(inv)}
                                        disabled={busyId === inv.token_prefix}
                                        data-testid={`resend-invite-${inv.token_prefix}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 disabled:opacity-50"
                                        title="Re-send the invite email and extend expiry by 7 days"
                                    >
                                        <Send className="w-3 h-3" /> Resend
                                    </button>
                                    <button
                                        onClick={() => cancelInvite(inv)}
                                        disabled={busyId === inv.token_prefix}
                                        data-testid={`cancel-invite-${inv.token_prefix}-btn`}
                                        className="brutal-btn-ghost text-[10px] py-1.5 px-2 text-red-400 disabled:opacity-50"
                                        title="Revoke this invite — the magic link stops working"
                                    >
                                        <X className="w-3 h-3" /> Cancel
                                    </button>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}
        </div>
    );
}
